export enum AsbestosDetection {
  ASBESTOS = 'ASBESTOS',
  TALK = 'TALK',
  KMF = 'KMF',
  KMF_NON_WHO = 'KMF_NON_WHO',
  NONE = 'NONE',
}

export const asbestosPercentagePmIds = [137537, 137736];
export const kmfPmIds = [137533, 137746, 137608];

export const asbestosPmIds = [137532, 137733, 137588];

export enum AsbestosParameterKind {
  ASBESTOS = 'ASBESTOS',
  KMF = 'KMF',
  ASBESTOS_PERCENTAGE = 'ASBESTOS_PERCENTAGE',
}

export const isAsbestosCategory = (category: string | undefined) => {
  return (
    category?.trim()?.toLowerCase() ===
    AsbestosParameterKind.ASBESTOS.toLowerCase()
  );
};

const mapRawToEnum = (value: string): AsbestosFinding | undefined => {
  return rawValueToEnum[value];
};

export const getTypedFinding = (
  pmId: string | undefined,
  rawFinding: string | undefined,
  category: string | undefined = undefined,
) => {
  return getTypedResult(pmId, rawFinding, category)?.finding;
};

export const getTypedVal = (
  pmId: string | undefined,
  rawFinding: string | undefined,
  category: string | undefined = undefined,
) => {
  return getTypedResult(pmId, rawFinding, category)?.val;
};

export const getTypedValFromEnumWithCategory = (
  pmId: string | undefined,
  enumStringified: string | undefined,
  category: AsbestosParameterKind,
) => {
  if (!pmId || !enumStringified) {
    return undefined;
  }
  const typedFinding =
    AsbestosFinding[enumStringified as keyof typeof AsbestosFinding];

  return getTypedResultFromAsbestosFinding(pmId, typedFinding, category);
};

export const getTypedValFromEnum = (
  pmId: string | undefined,
  enumStringified: string | undefined,
  category: string | undefined = undefined,
) => {
  if (!pmId || !enumStringified) {
    return undefined;
  }

  const typedFinding =
    AsbestosFinding[enumStringified as keyof typeof AsbestosFinding];

  return getTypedResultFromAsbestosFinding(pmId, typedFinding, category);
};

const getTypedResult = (
  pmId: string | undefined,
  rawFinding: string | undefined,
  category: string | undefined,
) => {
  if (!rawFinding) {
    return undefined;
  }

  const typedFinding = mapRawToEnum(rawFinding);
  return getTypedResultFromAsbestosFinding(pmId, typedFinding, category);
};

const getTypedResultFromAsbestosFinding = (
  pmId: string | undefined,
  asbestosFinding: AsbestosFinding | undefined,
  category: string | undefined,
) => {
  if (!asbestosFinding || !pmId) {
    return undefined;
  }
  const pmIdNumber = parseInt(pmId, 10);
  const findingGroup = possibleAsbestosFindings.find(
    (x) =>
      x.pmId.includes(pmIdNumber) && (!category || x.category === category),
  );

  return findingGroup?.values?.find((x) => x.val === asbestosFinding);
};

export const isAsbestosPmId = (pmId: string | undefined) => {
  if (!pmId) {
    return false;
  }

  const pmIdNumber = parseInt(pmId, 10);
  return possibleAsbestosFindings.some((x) => x.pmId.includes(pmIdNumber));
};

export const isPercentage = (pmId: string | undefined) => {
  if (!pmId) {
    return false;
  }

  const pmIdNumber = parseInt(pmId, 10);
  return possibleAsbestosFindings.some(
    (x) =>
      x.pmId.includes(pmIdNumber) &&
      x.category === AsbestosParameterKind.ASBESTOS_PERCENTAGE,
  );
};

export enum AsbestosFinding {
  // Asbestos detection
  CHRYSOTIL_DETECTED = 'CHRYSOTIL_DETECTED', // "Chrysotilasbest nachgewiesen"
  CHRYSOTIL_AMPHIBOLE_ANTHOPHYLLITE_DETECTED = 'CHRYSOTIL_AMPHIBOLE_ANTHOPHYLLITE_DETECTED', // "Chrysotil- und Amphibolasbest (Anthophyllit) nachgewiesen"
  CHRYSOTIL_AMPHIBOLE_CROCIDOLITE_DETECTED = 'CHRYSOTIL_AMPHIBOLE_CROCIDOLITE_DETECTED', // "Chrysotil- und Amphibolasbest (Krokydolith) nachgewiesen"
  CHRYSOTIL_AMPHIBOLE_AMOSITE_DETECTED = 'CHRYSOTIL_AMPHIBOLE_AMOSITE_DETECTED', // "Chrysotil- und Amphibolasbest (Amosit) nachgewiesen"
  CHRYSOTIL_AMPHIBOLE_TREMOLITE_DETECTED = 'CHRYSOTIL_AMPHIBOLE_TREMOLITE_DETECTED', // "Chrysotil- und Amphibolasbest (Tremolit) nachgewiesen"
  CHRYSOTIL_AMPHIBOLE_ACTINOLITE_DETECTED = 'CHRYSOTIL_AMPHIBOLE_ACTINOLITE_DETECTED', // "Chrysotil- und Amphibolasbest (Aktinolith) nachgewiesen"
  NO_ASBESTOS_DETECTED = 'NO_ASBESTOS_DETECTED', // "Asbest nicht nachgewiesen"
  NO_ASBESTOS_INORGANIC_FIBER = 'NO_ASBESTOS_INORGANIC_FIBER', // "Asbest nicht nachgewiesen (sonstige anorganische Faser)"
  NO_ASBESTOS_ORGANIC_FIBER = 'NO_ASBESTOS_ORGANIC_FIBER', // "Asbest nicht nachgewiesen (organische Faser)"
  NO_ASBESTOS_ORGANIC_FIBER_ALT = 'NO_ASBESTOS_ORGANIC_FIBER_ALT', // "Asbest nicht nachgewiesen (sonstige Fasern nachgewiesen)"
  NO_ASBESTOS_TALK_DETECTED = 'NO_ASBESTOS_TALK_DETECTED', // "Asbest nicht nachgewiesen (Talk nachgewiesen)"
  AMPHIBOLE_TREMOLITE_DETECTED = 'AMPHIBOLE_TREMOLITE_DETECTED', // "Amphibolasbest (Tremolit) nachgewiesen"
  AMPHIBOLE_AMOSITE_DETECTED = 'AMPHIBOLE_AMOSITE_DETECTED', // "Amphibolasbest (Amosit) nachgewiesen"
  AMPHIBOLE_ANTHOPHYLLITE_DETECTED = 'AMPHIBOLE_ANTHOPHYLLITE_DETECTED', // "Amphibolasbest (Anthophyllit) nachgewiesen"
  AMPHIBOLE_CROCIDOLITE_DETECTED = 'AMPHIBOLE_CROCIDOLITE_DETECTED', // "Amphibolasbest (Krokydolith) nachgewiesen"
  AMPHIBOLE_ACTINOLITE_DETECTED = 'AMPHIBOLE_ACTINOLITE_DETECTED', // "Amphibolasbest (Aktinolith) nachgewiesen"
  // Amounts
  TRACE = 'TRACE', // "Spuren"
  MINUS = 'MINUS',
  PERCENT_1_TO_5 = 'PERCENT_1_TO_5', // "1-5 %"
  PERCENT_5_TO_20 = 'PERCENT_5_TO_20', // "5-20 %"
  PERCENT_20_TO_50 = 'PERCENT_20_TO_50', // "20-50 %"
  PERCENT_OVER_50 = 'PERCENT_OVER_50', // "> 50 %"
  NOT_POSSIBLE = 'NOT_POSSIBLE', // "nicht möglich"

  // Artificial Mineral Fibers (AMF)
  AMF_NOT_DETECTED = 'AMF_NOT_DETECTED', // "KMF nicht nachgewiesen"
  AMF_DETECTED_NON_WHO = 'AMF_DETECTED_NON_WHO', // "KMF nachgewiesen (keine WHO-Fasern)"
  AMF_DETECTED_WHO = 'AMF_DETECTED_WHO', // "KMF nachgewiesen (WHO-Fasern)"
}

const rawValueToEnum: Record<string, AsbestosFinding> = {
  'Chrysotilasbest nachgewiesen': AsbestosFinding.CHRYSOTIL_DETECTED,
  'Chrysotil- und Amphibolasbest (Anthophyllit) nachgewiesen':
    AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ANTHOPHYLLITE_DETECTED,
  'Chrysotil- und Amphibolasbest (Krokydolith) nachgewiesen':
    AsbestosFinding.CHRYSOTIL_AMPHIBOLE_CROCIDOLITE_DETECTED,
  'Chrysotil- und Amphibolasbest (Amosit) nachgewiesen':
    AsbestosFinding.CHRYSOTIL_AMPHIBOLE_AMOSITE_DETECTED,
  'Chrysotil- und Amphibolasbest (Tremolit) nachgewiesen':
    AsbestosFinding.CHRYSOTIL_AMPHIBOLE_TREMOLITE_DETECTED,
  'Chrysotil- und Amphibolasbest (Aktinolith) nachgewiesen':
    AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ACTINOLITE_DETECTED,
  'Asbest nicht nachgewiesen': AsbestosFinding.NO_ASBESTOS_DETECTED,
  'Asbest nicht nachgewiesen (sonstige anorganische Faser)':
    AsbestosFinding.NO_ASBESTOS_INORGANIC_FIBER,
  'Asbest nicht nachgewiesen (organische Faser)':
    AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER,
  'Asbest nicht nachgewiesen (sonstige Fasern nachgewiesen)':
    AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER_ALT,
  'Asbest nicht nachgewiesen (Talk nachgewiesen)':
    AsbestosFinding.NO_ASBESTOS_TALK_DETECTED,
  'Amphibolasbest (Tremolit) nachgewiesen':
    AsbestosFinding.AMPHIBOLE_TREMOLITE_DETECTED,
  'Amphibolasbest (Amosit) nachgewiesen':
    AsbestosFinding.AMPHIBOLE_AMOSITE_DETECTED,
  'Amphibolasbest (Anthophyllit) nachgewiesen':
    AsbestosFinding.AMPHIBOLE_ANTHOPHYLLITE_DETECTED,
  'Amphibolasbest (Krokydolith) nachgewiesen':
    AsbestosFinding.AMPHIBOLE_CROCIDOLITE_DETECTED,
  'Amphibolasbest (Aktinolith) nachgewiesen':
    AsbestosFinding.AMPHIBOLE_ACTINOLITE_DETECTED,
  'Spuren': AsbestosFinding.TRACE,
  '1-5 %': AsbestosFinding.PERCENT_1_TO_5,
  '5-20 %': AsbestosFinding.PERCENT_5_TO_20,
  '20-50 %': AsbestosFinding.PERCENT_20_TO_50,
  '> 50 %': AsbestosFinding.PERCENT_OVER_50,
  '>50 %': AsbestosFinding.PERCENT_OVER_50,
  'nicht möglich': AsbestosFinding.NOT_POSSIBLE,
  '-': AsbestosFinding.MINUS,
  'KMF nicht nachgewiesen': AsbestosFinding.AMF_NOT_DETECTED,
  'KMF nachgewiesen (keine WHO-Fasern)': AsbestosFinding.AMF_DETECTED_NON_WHO,
  'KMF nachgewiesen (WHO-Fasern)': AsbestosFinding.AMF_DETECTED_WHO,
};

const possibleAsbestosFindings: {
  category: AsbestosParameterKind;
  pmId: number[];
  values: {
    val: AsbestosFinding;
    finding: AsbestosDetection;
  }[];
}[] = [
  {
    category: AsbestosParameterKind.ASBESTOS,
    pmId: asbestosPmIds,
    values: [
      {
        val: AsbestosFinding.CHRYSOTIL_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ANTHOPHYLLITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.CHRYSOTIL_AMPHIBOLE_CROCIDOLITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.CHRYSOTIL_AMPHIBOLE_AMOSITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.CHRYSOTIL_AMPHIBOLE_TREMOLITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ACTINOLITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.NO_ASBESTOS_DETECTED,
        finding: AsbestosDetection.NONE,
      },
      {
        val: AsbestosFinding.NO_ASBESTOS_INORGANIC_FIBER,
        finding: AsbestosDetection.NONE,
      },
      {
        val: AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER,
        finding: AsbestosDetection.NONE,
      },
      {
        val: AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER_ALT,
        finding: AsbestosDetection.NONE,
      },
      {
        val: AsbestosFinding.NO_ASBESTOS_TALK_DETECTED,
        finding: AsbestosDetection.TALK,
      },
      {
        val: AsbestosFinding.AMPHIBOLE_TREMOLITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.AMPHIBOLE_AMOSITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.AMPHIBOLE_ANTHOPHYLLITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.AMPHIBOLE_CROCIDOLITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.AMPHIBOLE_ACTINOLITE_DETECTED,
        finding: AsbestosDetection.ASBESTOS,
      },
    ],
  },
  {
    category: AsbestosParameterKind.ASBESTOS_PERCENTAGE,
    pmId: asbestosPercentagePmIds,
    values: [
      { val: AsbestosFinding.TRACE, finding: AsbestosDetection.ASBESTOS },
      {
        val: AsbestosFinding.PERCENT_1_TO_5,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.PERCENT_5_TO_20,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.PERCENT_20_TO_50,
        finding: AsbestosDetection.ASBESTOS,
      },
      {
        val: AsbestosFinding.PERCENT_OVER_50,
        finding: AsbestosDetection.ASBESTOS,
      },
      { val: AsbestosFinding.NOT_POSSIBLE, finding: AsbestosDetection.NONE },
      { val: AsbestosFinding.MINUS, finding: AsbestosDetection.NONE },
    ],
  },
  {
    category: AsbestosParameterKind.KMF,
    pmId: kmfPmIds,
    values: [
      {
        val: AsbestosFinding.AMF_NOT_DETECTED,
        finding: AsbestosDetection.NONE,
      },
      {
        val: AsbestosFinding.AMF_DETECTED_NON_WHO,
        finding: AsbestosDetection.KMF_NON_WHO,
      },
      { val: AsbestosFinding.AMF_DETECTED_WHO, finding: AsbestosDetection.KMF },
    ],
  },
];
